import moment from 'moment';
import React from 'react';
import { useNavigate } from 'react-router';
import type { IFlightLog } from '../../../../common/src/domain/flightLog/IFlightLog';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import { openApiFetchDelete, openApiFetchGet } from '../../store/openApiFetch';
import { AssetTypes } from '../../utils/enums/AssetTypes';
import { Roles } from '../../utils/enums/Roles';
import { SortColumn, SortDirections } from '../../utils/sorting';
import { IconEditButton } from '../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import {
    EditPanelButtons,
    PanelBody,
    PanelButtonLink,
    PanelHeading,
    Table,
    TableBody,
    TableButtons,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    TableSortableHeader,
} from '../elements';
import { Spinner } from '../spinners';
import { useUserHasRole } from '../utils/authorization/useUserHasRole';
import { ListPager } from '../utils/ListPager';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import { useReactAsync } from '../utils/useReactAsync';
import { useSearch } from '../utils/useSearch';
import { EditFlightLogModal } from './EditFlightLogModal';
import { AssetDropdownItem, Equipment, ProjectBase, SurveyData } from './interfaces';

type Pagination = { limit: string; offset: string; sortColumns: SortColumn[] };
interface IProps {
    // is passed here when we are in 'Edit Survey' page
    surveyId?: string;
    // is passed here when we are in 'Flightlog equipment' page
    equipmentId?: string;
    // used at dashboard to overcome 'double header' issue
    header?: string;
    // used at dashboard to specify custom amount of items per page
    itemsPerPage?: number;
    // NOTE next two may appear only when we are in 'Edit Survey' page
    equipment?: Equipment[];
    surveyedOn?: Date;
}

const defaultItemsPerPage = 10;

const loadAssetsMetadata = async (surveyId: string): Promise<AssetDropdownItem[]> => {
    const types = AssetTypes.SurveyVideo;
    const result = await fetchGet('/api/assets', { surveyId, types });
    return (result.json as { id: string; duration: number; name: string }[]).map((item) => ({
        value: item.id,
        text: item.name,
        duration: item.duration,
    }));
};

const loadSurveysProjects = async (surveyId: string): Promise<ProjectBase[]> => {
    const result = await openApiFetchGet('/api/surveys/{surveyId}/projects', { surveyId });
    return result ?? [];
};

async function loadFlightLogBySurvey(query: Pagination, surveyId: string) {
    const params = { surveyId, ...query };
    return await openApiFetchGet('/api/surveys/{surveyId}/flightlogs', params);
}

async function loadFlightLogsByEquipment(query: Pagination, equipmentId: string) {
    const url = '/api/equipment/{equipmentId}/flightLog';
    const params = { equipmentId, ...query };
    return await openApiFetchGet(url, params);
}

async function loadFlightLogsByUser(query: Pagination) {
    const url = '/api/flightLogUser';
    return await openApiFetchGet(url, query);
}

type LoadFlightLogsParamsType = {
    query: Pagination;
    loadDataBy: {
        surveyId: string | undefined;
        equipmentId: string | undefined;
    };
};

async function loadFlightLogs({ loadDataBy, query }: LoadFlightLogsParamsType) {
    let result;

    // case for 'Edit Survey' page/surveyId
    if (loadDataBy.surveyId !== undefined) {
        result = await loadFlightLogBySurvey(query, loadDataBy.surveyId);
        // case for 'Flightlog equipment' page/equipmentId
    } else if (loadDataBy.equipmentId !== undefined) {
        result = await loadFlightLogsByEquipment(query, loadDataBy.equipmentId);
        // case for 'Dashboard' page
    } else {
        result = await loadFlightLogsByUser(query);
    }

    result?.items.forEach((item) => {
        item.startTime = getFormattedTime(item.startTime);
        item.endTime = getFormattedTime(item.endTime);
    });

    return result;
}

const getFormattedTime = (timeStr: string | null | undefined): string => (timeStr ? moment(timeStr, 'HH:mm:ss').format('HH:mm') : '');

const getFormattedTimeRange = (item: IFlightLog): string =>
    [item.startTime, item.endTime]
        .map(getFormattedTime)
        .filter((str) => str !== '')
        .join(' - ');

export function FlightLogs(props: IProps) {
    const navigate = useNavigate();
    const translate = useTranslate();
    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const { surveyId, equipmentId } = props;
    const desiredItemsPerPage = props.itemsPerPage ?? defaultItemsPerPage;
    const isUsedWithinSurveyPage = surveyId !== undefined;
    const isUsedWithinEquipmentPage = equipmentId !== undefined;
    const isUsedWithinDashboardPage = !isUsedWithinSurveyPage && !isUsedWithinEquipmentPage;

    const { searchParameters, handleSortChanged, handleNavigation, queryObject, itemsPerPage } = useSearch(
        {},
        [{ columnName: 'date', direction: SortDirections.Descending }],
        desiredItemsPerPage,
    );

    const args = { query: queryObject, loadDataBy: { surveyId, equipmentId } };
    const { data, reload, isLoading } = useReactAsync(loadFlightLogs, args, [queryObject, surveyId, equipmentId]);

    const [isFlightLogAddVisible, setIsFlightLogAddVisible] = React.useState(false);
    const [flightLogToEdit, setFlightLogToEdit] = React.useState<IFlightLog>();

    const [assetsMetadata, setAssetsMetadata] = React.useState<AssetDropdownItem[]>([]);
    const [projects, setProjects] = React.useState<ProjectBase[]>([]);
    const [surveyData, setSurveyData] = React.useState<SurveyData>({});

    React.useEffect(() => {
        const loadAndStoreData = async (parentSurveyId: string) => {
            setAssetsMetadata(await loadAssetsMetadata(parentSurveyId));
            setProjects(await loadSurveysProjects(parentSurveyId));
        };

        if (isUsedWithinSurveyPage) {
            void loadAndStoreData(surveyId);
        }
    }, [surveyId, isUsedWithinSurveyPage]);

    const resetSurveyData = () => setSurveyData({});

    const showEditModalForNewCase = () => {
        setIsFlightLogAddVisible(true);
        if (isUsedWithinSurveyPage) {
            setSurveyData({ equipment: props.equipment!, surveyId });
        } else {
            resetSurveyData();
        }
    };

    const showModalForUpdateCase = (flightLog: IFlightLog) => {
        setFlightLogToEdit(flightLog);
        setIsFlightLogAddVisible(true);
    };

    const showModalForAssetsCase = () => {
        setIsFlightLogAddVisible(true);
        setSurveyData({
            assetsMetadata,
            equipment: props.equipment!,
            surveyId: surveyId!,
            surveyedOn: props.surveyedOn!,
        });
    };

    const showModalForProjectCase = () => {
        setIsFlightLogAddVisible(true);
        setSurveyData({
            equipment: props.equipment!,
            projects,
            surveyId: surveyId!,
        });
    };

    const hideEditModal = () => {
        setIsFlightLogAddVisible(false);
        setFlightLogToEdit(undefined);
        resetSurveyData();
    };

    async function removeFlightLog(flightLogId: string) {
        await openApiFetchDelete('/api/flightLog/{flightLogId}', { flightLogId });
        await reload();
    }

    async function saveFlightLog() {
        hideEditModal();
        resetSurveyData();
        await reload();
    }

    const onRowClickHandler = (flightLog: any) => {
        if (typeof flightLog.survey?.id === 'string') {
            navigate(`/surveys/${flightLog.survey.id}`);
        }
    };

    const deleteAllLogsViaConfirmation = async () => {
        if (!window.confirm(translate('flightLog.confirmDeleteAll'))) {
            return;
        }

        if (surveyId) {
            await openApiFetchDelete('/api/surveys/{surveyId}/flightLogs/deleteAll', { surveyId });
            await reload();
        }
    };

    const headerText = props.header ?? translate('flightLog.log');
    const isHeaderRenderable = headerText !== '' || (data?.total ?? 0) > itemsPerPage;

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <>
            {isHeaderRenderable && (
                <PanelHeading text={headerText} icon="fa fa-book">
                    {/* At dashboard page we need to show only one page.
                so paginator is useless there in any case */}
                    {!isUsedWithinDashboardPage && (
                        <ListPager
                            disabled={isLoading}
                            itemsPerPage={itemsPerPage}
                            inPanel={true}
                            total={data?.total ?? 0}
                            page={searchParameters.page}
                            handleNavigation={handleNavigation}
                        />
                    )}
                </PanelHeading>
            )}
            <PanelBody noPadding>
                <Table spaced={false} hover={isUsedWithinDashboardPage}>
                    <TableHead>
                        <TableHeadRow light={true}>
                            <TableSortableHeader
                                header={translate('common.date')}
                                columnName="date"
                                sortColumns={searchParameters.sortColumns}
                                onClick={handleSortChanged}
                            />
                            {isUsedWithinEquipmentPage && <TableHeader>{translate('flightLog.pilot')}</TableHeader>}
                            {!isUsedWithinEquipmentPage && <TableHeader>{translate('flightLog.equipment')}</TableHeader>}
                            <TableHeader>{translate('common.time')} </TableHeader>
                            <TableHeader>{translate('flightLog.duration')}</TableHeader>
                            {!isUsedWithinSurveyPage && <TableHeader>{translate('project.project')}</TableHeader>}
                            {!isUsedWithinDashboardPage && <TableHeader>&nbsp;</TableHeader>}
                        </TableHeadRow>
                    </TableHead>
                    <TableBody>
                        {data &&
                            data.items.map((item: any) => (
                                <TableRow
                                    key={item.id}
                                    // presence of 'onClick' attribute makes mouse pointer look like hand
                                    // so 'onClick' attribute should be only in some cases
                                    {...(isUsedWithinDashboardPage && { onClick: () => onRowClickHandler(item) })}
                                >
                                    <TableData nowrap>
                                        {' '}
                                        <NullableFormattedDate value={item.date} />{' '}
                                    </TableData>
                                    {isUsedWithinEquipmentPage && (
                                        <TableData>
                                            {item.users.firstName} {item.users.lastName}
                                        </TableData>
                                    )}
                                    {!isUsedWithinEquipmentPage && <TableData>{item.equipment.name}</TableData>}
                                    <TableData nowrap>{getFormattedTimeRange(item)}</TableData>
                                    <TableData>{translate('flightLog.mins', item.flightDuration.toString())}</TableData>
                                    {!isUsedWithinSurveyPage && (
                                        <TableData>
                                            {' '}
                                            {item.survey.object.ownerCompany.name} - {item.survey.object.name}
                                        </TableData>
                                    )}
                                    {!isUsedWithinDashboardPage && (
                                        <TableButtons>
                                            <IconEditButton onClick={() => showModalForUpdateCase(item)} />
                                            <IconRemoveButton
                                                onClick={async () => removeFlightLog(item.id)}
                                                removeQuestion={translate('common.confirmDelete', item.startTime || item.flightDuration.toString())}
                                            />
                                        </TableButtons>
                                    )}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </PanelBody>

            {!isUsedWithinDashboardPage && (
                <>
                    <EditPanelButtons footer>
                        {isAdmin && (
                            <PanelButtonLink onClick={deleteAllLogsViaConfirmation} small={true} text={translate('flightLog.deleteAllInSurvey')} mt />
                        )}
                        {assetsMetadata.length > 0 && (
                            <PanelButtonLink onClick={showModalForAssetsCase} small={true} text={translate('flightLog.generateLogFromVideos')} mt />
                        )}

                        {projects.length > 0 && (
                            <PanelButtonLink
                                onClick={showModalForProjectCase}
                                small={true}
                                text={translate('flightLog.generateLogsFromMeasurements')}
                                mt
                            />
                        )}

                        <PanelButtonLink
                            onClick={showEditModalForNewCase}
                            small={true}
                            primary={true}
                            text={translate('common.addItem', translate('flightLog.entry'))}
                            mt
                            icon="fa fa-plus"
                        />
                    </EditPanelButtons>

                    {isFlightLogAddVisible && (
                        <EditFlightLogModal
                            onClose={hideEditModal}
                            onSave={saveFlightLog}
                            flightLog={flightLogToEdit}
                            surveyData={surveyData}
                            equipmentId={props.equipmentId ?? flightLogToEdit?.equipmentId}
                        />
                    )}
                </>
            )}
        </>
    );
}
